/**
 * Persistent per-file "viewed" marks for a pull request review.
 *
 * Marks are keyed by repository and pull number — deliberately not by head
 * SHA — so a push never forgets what the reviewer already looked at. Each
 * mark remembers a fingerprint of the diff it was made against; when the
 * file's diff changes on a later head the mark degrades to "changed" instead
 * of silently claiming the new code was reviewed.
 */

export interface ViewedMark {
  fingerprint: string;
  markedAt: string;
}

export type ViewedFileState = "viewed" | "changed";

const STORAGE_PREFIX = "chew:viewed:v1:";

export function viewedStorageKey(repository: string, pullNumber: number): string {
  return `${STORAGE_PREFIX}${repository}#${pullNumber}`;
}

export function loadViewedFiles(key: string): Record<string, ViewedMark> {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    const marks: Record<string, ViewedMark> = {};
    for (const [path, value] of Object.entries(parsed)) {
      if (
        value && typeof value === "object" &&
        typeof (value as ViewedMark).fingerprint === "string" &&
        typeof (value as ViewedMark).markedAt === "string"
      ) marks[path] = value as ViewedMark;
    }
    return marks;
  } catch {
    return {};
  }
}

export function saveViewedFiles(key: string, marks: Record<string, ViewedMark>): void {
  try {
    if (Object.keys(marks).length === 0) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(marks));
  } catch {
    // Storage may be full or unavailable; the in-memory marks still apply.
  }
}

/** Cheap, stable fingerprint of a file's diff inputs (djb2 over both sides). */
export function fileContentFingerprint(file: { base: string; head: string }): string {
  let hash = 5381;
  const text = `${file.base.length}\u0000${file.base}\u0000${file.head.length}\u0000${file.head}`;
  for (let index = 0; index < text.length; index += 1) {
    hash = ((hash * 33) ^ text.charCodeAt(index)) >>> 0;
  }
  return hash.toString(36);
}

export function viewedFileState(mark: ViewedMark | undefined, fingerprint: string | undefined): ViewedFileState | undefined {
  if (!mark) return undefined;
  if (fingerprint === undefined) return "viewed";
  return mark.fingerprint === fingerprint ? "viewed" : "changed";
}
